import { Component, EventEmitter, inject, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LocationService } from 'src/app/Common/LocationService/location.service';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { CitiesDTO } from 'src/app/Reference/cities/cities.interface';
import { CountryDTO } from 'src/app/Reference/country/country.interface';
import { StateDTO } from 'src/app/Reference/state/state.interface';
import { ColorColumns } from 'src/app/Shared/color-table/color-table.interface';
import { accordionAnimation } from '../animations/animations';
import { ConfirmmodalserviceService } from '../confirm-delete-modal/confirmmodalservice.service';
import { AddressDetailCreateRequestDTO, AddressDetailDeleteRequestDTO, AddressDetailDTO, AddressDetailReadByMasterIdRequestDTO, AddressDetailUpdateRequestDTO } from './address-detail.interface';
import { AddressDetailService } from './address-detail.service';
import { ValueListService } from 'src/app/Common/ValueListService/value-list.service';
import { KeyValueRequestDTO } from 'src/app/Common/ValueListService/valuelist.interface';
import { GetDictionaryDTO } from 'src/app/Common/common.interface';
import { ValueListItemDTO, ValueListItemReadByVlCodeRequestDTO } from 'src/app/Common/ValueListService/ValueListItem.interface';
import { Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ensureNumber, ensureString } from '../customFunctions/CustomFunctions';

@Component({
  selector: 'app-address-detail',
  templateUrl: './address-detail.component.html',
  styleUrls: ['./address-detail.component.css'],
  animations: [accordionAnimation]
})
export class AddressDetailComponent implements OnInit {

  // Inputs
  @Input({ required: true }) masterType: string = ''
  @Input({ required: true }) masterId: number = 0
  @Input() editMode: boolean = false
  @Input() calledInUIC: boolean = false

  /**
   * Value List code backing the Address Type dropdown.
   * Defaults to 'AT' so Supplier / Customer / Company pages keep using the
   * existing value list. Staff Detail page passes 'IAT' (Individual Address Type).
   */
  @Input() addressTypeVlCode: string = 'AT'

  /**
   * Embedded "add one address" mode for other forms (e.g. the visit scheduler): hides the toolbar and the
   * card / list views, opens the form straight away, creates the address through the API and reports it via
   * addressSaved. Cancel is reported via addressCancelled. Everything else behaves as before.
   */
  @Input() formOnly: boolean = false

  /** Makes City mandatory in the form. Off by default, so every other page keeps City optional. */
  @Input() requireCity: boolean = false

  // Outputs
  @Output() AddressDetailFormEmit: EventEmitter<AddressDetailDTO[]> = new EventEmitter()
  /** formOnly mode: the address that was just created. */
  @Output() addressSaved: EventEmitter<AddressDetailDTO> = new EventEmitter()
  /** formOnly mode: the user closed the form without saving. */
  @Output() addressCancelled: EventEmitter<void> = new EventEmitter()

  // Flags
  currentView: string = 'card';
  isDataLoading: boolean = false;

  private destroy$ = new Subject<void>();

  // Services 
  addressService = inject(AddressDetailService)
  locationService = inject(LocationService)
  modalService = inject(NgbModal);
  private confirmModal = inject(ConfirmmodalserviceService)
  User = inject(UserLoginService).User()
  valuelistService = inject(ValueListService)
  toastr = inject(ToastrService)

  statesList: StateDTO[] = []
  citiesList: CitiesDTO[] = []

  /**
   * Options shown in the Address Type dropdown. Populated from the value list
   * identified by addressTypeVlCode; falls back to defaultAddressTypes when that
   * value list has not been configured for the tenant/branch.
   */
  addressTypeOptions: string[] = []

  /** Hard-coded fallback, used only when the value list returns no items. */
  readonly defaultAddressTypes: string[] = [
    'Current Address',
    'Permanent Address',
    'Native Address',
    'Correspondence Address',
    'Temporary Address',
    'Other'
  ]

  tableCol: ColorColumns[] = [
    {
      colName: 'Type',
      colData: 'addressType',
    },
    {
      colName: 'Address',
      colData: 'address',
    },
    {
      colName: 'City',
      colData: 'city',
    },
    {
      colName: 'State',
      colData: 'state',
    },
    {
      colName: 'Pincode',
      colData: 'pincode',
    },
    {
      colName: 'Default',
      colData: 'isDefault'
    }
  ];

  AddressList: AddressDetailDTO[] = []
  addressDetailForm = new FormGroup({
    masterId: new FormControl(0),
    masterType: new FormControl(''),
    addressId: new FormControl(0),
    addressType: new FormControl('', [Validators.required]),
    address: new FormControl('', [Validators.required, Validators.maxLength(2000)]),
    city: new FormControl(''),
    state: new FormControl(''),
    country: new FormControl(''),
    pincode: new FormControl(''),
    status: new FormControl(0),
    gstNo: new FormControl(''),
    otherDetail1: new FormControl(''),
    otherDetail2: new FormControl(''),
    otherDetail3: new FormControl(''),
    otherDetail4: new FormControl(''),
    otherDetail5: new FormControl(''),
    otherDetail6: new FormControl(''),
    isActive: new FormControl(0),
    isDefault: new FormControl(0),
    actionUser: new FormControl('')
  });
  country: CountryDTO | any;

  // Add setView method for toggle functionality
  setView(view: string) {
    this.currentView = view;
  }

  /**
   * Normalises the isDefault checkbox value (which binds as boolean) into the
   * 0/1 integer flag the API and DB column expect.
   */
  private toFlag(value: unknown): number {
    return value === true || value === 1 || value === '1' ? 1 : 0;
  }

  ngOnInit(): void {


    // ✅ Setup table columns
    this.locationService.cities$.subscribe(res => {
      this.citiesList = res.filter(i => {
        if (i.stateId == 14) {
          return i
        }
        return
      })
    })

    this.locationService.states$.subscribe(res => {
      this.statesList = res
    })
    this.getAddressType()
    if (this.requireCity) {
      this.addressDetailForm.controls.city.setValidators([c => (String(c.value ?? '').trim() ? null : { required: true })])
      this.addressDetailForm.controls.city.updateValueAndValidity()
    }
    if (this.formOnly) this.addAddress()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.formOnly) return   // embedded "add one" mode never lists existing addresses
    if (changes['masterId']) {
      if (this.calledInUIC) {
        console.log(this.masterId);
        console.log(this.masterType);
        this.currentView = 'list';
        this.editMode = false;

        // UIC Mode: Load addresses only if masterId > 0 (edit mode)
        if (this.masterId && this.masterId > 0) {
          this.loadAddressesForEdit();
        } else {
          // New customer - just initialize with empty list
          this.AddressList = [];
        }
      } else {
        // Normal mode: Load addresses from API only if masterId > 0
        if (this.masterId && this.masterId > 0) {
          this.loadAddressDetails();
        }
      }
    }
  }

  getAddressType() {
    this.valuelistService.getItemByVLCode(this.addressTypeVlCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.addressTypeOptions = this.toOptions(res.valueListItems, this.defaultAddressTypes)
        },
        error: (err) => {
          console.error(`Error loading address types for VlCode ${this.addressTypeVlCode}:`, err)
          this.addressTypeOptions = [...this.defaultAddressTypes]
        }
      })
  }

  /**
   * Maps value list items to their display names, falling back to the hard-coded
   * list when the value list is missing or has no usable items.
   */
  private toOptions(items: ValueListItemDTO[] | undefined, fallback: string[]): string[] {
    const names = (items ?? [])
      .map(i => (i.vliName ?? '').trim())
      .filter(n => n.length > 0)
    return names.length ? names : [...fallback]
  }


  loadAddressesForEdit() {
    let data: AddressDetailReadByMasterIdRequestDTO = {
      masterId: this.masterId,
      masterType: this.masterType,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId
    };

    this.addressService.AddressDetail_readByMasterId(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.AddressList = res.items || [];
          console.log('Loaded addresses for edit:', this.AddressList);
          this.emitAddresses();
        },
        error: (err) => {
          console.error('Error loading addresses:', err);
          this.AddressList = [];
        }
      });
  }

  loadAddressDetails() {
    let data: AddressDetailReadByMasterIdRequestDTO = {
      masterId: this.masterId,
      masterType: this.masterType,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId
    }

    this.isDataLoading = true
    this.addressService.AddressDetail_readByMasterId(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.AddressList = res.items ?? []
          this.isDataLoading = false
        },
        error: (err) => {
          console.error('Error loading addresses:', err)
          this.isDataLoading = false
        }
      })
  }

  handleActionClick(event: { actionName: string; rowData: AddressDetailDTO; }) {
    let { actionName, rowData } = event
    if (actionName == 'Edit') {
      this.editMode = true
      this.addressDetailForm.patchValue({
        ...rowData
      })
    } else if (actionName == 'Delete') {
      this.confirmModal
        .openDeleteModal(event.rowData.addressType!, event.rowData)
        .subscribe((res) => {
          if (res) {
            if (this.calledInUIC) {
              const index = this.AddressList.findIndex(a => a.addressId === event.rowData.addressId);
              if (index < 0) return;

              if (event.rowData.addressId && event.rowData.addressId > 0) {
                // Existing address (already persisted) - must call the API, otherwise
                // it only disappears from this local list and reappears next time the
                // addresses are reloaded from the backend.
                const data: AddressDetailDeleteRequestDTO = {
                  addressId: event.rowData.addressId,
                  actionUser: String(this.User.userId),
                  tenantId: this.User.companyId,
                  tenantBranchId: this.User.loggedInBranchId
                };
                this.addressService.AddressDetail_delete(data).subscribe({
                  next: () => {
                    this.AddressList.splice(index, 1);
                    this.toastr.success('Address removed');
                    this.closeEditFormIfEditing(event.rowData.addressId);
                  },
                  error: (err) => {
                    console.error('Error deleting address:', err);
                    this.toastr.error('Failed to delete address');
                  }
                });
              } else {
                // Temp (unsaved) address - just drop it locally and re-emit
                this.AddressList.splice(index, 1);
                this.toastr.success('Address removed');
                this.emitAddresses();
                this.closeEditFormIfEditing(event.rowData.addressId);
              }
            } else {
              // Normal mode: Call API
              const data: AddressDetailDeleteRequestDTO = {
                addressId: event.rowData.addressId ?? 0,
                actionUser: String(this.User.userId),
                tenantId: this.User.companyId,
                tenantBranchId: this.User.loggedInBranchId
              };
              this.addressService.AddressDetail_delete(data).subscribe({
                next: (res) => {
                  this.closeEditFormIfEditing(event.rowData.addressId);
                  this.loadAddressDetails()
                },
                error: (err) => {
                  console.error('Error deleting address:', err);
                }
              })
            }
          }

        })
    } else if (actionName == 'Default') {
      this.defaultCheckBox({ rowData, isChecked: rowData.isDefault !== 1 });
    }
  }

  // If the row being deleted is the one currently patched into the edit form, close the
  // form - otherwise a Submit right after would still hold the deleted addressId and fire
  // an Update against a record that no longer exists.
  private closeEditFormIfEditing(deletedAddressId: number | string | undefined | null): void {
    if (this.editMode && this.addressDetailForm.get('addressId')?.value === deletedAddressId) {
      this.resetForm();
    }
  }



  submitForm() {
    this.addressDetailForm.markAllAsTouched()
    if (this.addressDetailForm.valid) {
      let formData = this.addressDetailForm.getRawValue()
      const isDefaultFlag = this.toFlag(formData.isDefault)

      let payload: AddressDetailUpdateRequestDTO = {
        addressId: formData.addressId ?? 0,
        masterId: formData.masterId ?? 0,
        masterType: formData.masterType ?? '',
        addressType: formData.addressType ?? '',
        address: formData.address ?? '',
        city: formData.city ?? '',
        state: formData.state ?? '',
        country: formData.country ?? '',
        pincode: formData.pincode ?? '',
        status: formData.status ?? 0,
        gstNo: formData.gstNo || '',
        otherDetail1: formData.otherDetail1 || '',
        otherDetail2: formData.otherDetail2 || '',
        otherDetail3: formData.otherDetail3 || '',
        otherDetail4: formData.otherDetail4 || '',
        otherDetail5: formData.otherDetail5 || '',
        otherDetail6: formData.otherDetail6 || '',
        isActive: 1,
        isDefault: isDefaultFlag,
        actionUser: this.User.userId.toString(),
        tenantId: this.User.companyId,
        tenantBranchId: this.User.loggedInBranchId
      };

      if (formData.addressId && formData.addressId !== 0) {
        // Guard against submitting an update for a row that was deleted while this
        // form was still open (e.g. edit -> delete the same row -> submit).
        if (!this.AddressList.some(a => a.addressId === formData.addressId)) {
          this.toastr.error('This address was deleted and can no longer be updated');
          this.resetForm();
          return;
        }

        // Update mode - existing address with real ID, call API directly
        this.addressService.AddressDetail_update(payload).subscribe(res => {
          this.addressDetailForm.reset()
          this.AddressList = this.AddressList.map(i =>
            i.addressId === res.addressId ? { ...i, ...res } : i
          );
          this.editMode = false
          this.toastr.success('Address updated');
          // Marking this address default clears the flag on the sibling rows
          // server-side, so resync to avoid showing two "Default" badges.
          if (isDefaultFlag === 1 && !this.calledInUIC) {
            this.loadAddressDetails();
          }
        })
        this.editMode = false;
      } else {
        // Create mode
        if (this.calledInUIC && this.masterId > 0) {
          // UIC Mode with existing masterId (editing company): Create immediately via API
          let data: AddressDetailCreateRequestDTO = {
            masterId: this.masterId,
            masterType: this.masterType,
            addressType: payload.addressType ?? '',
            address: payload.address ?? '',
            city: payload.city ?? '',
            state: payload.state,
            country: payload.country,
            pincode: payload.pincode ?? '',
            status: payload.status ?? 1,
            gstNo: payload.gstNo || '',
            otherDetail1: payload.otherDetail1 || '',
            otherDetail2: payload.otherDetail2 || '',
            otherDetail3: payload.otherDetail3 || '',
            otherDetail4: payload.otherDetail4 || '',
            otherDetail5: payload.otherDetail5 || '',
            otherDetail6: payload.otherDetail6 || '',
            actionUser: this.User.userId.toString(),
            isDefault: payload.isDefault,
            tenantId: this.User.companyId,
            tenantBranchId: this.User.loggedInBranchId
          };
          this.addressService.AddressDetail_create(data).subscribe({
            next: (res) => {
              this.AddressList.push(res);
              this.addressDetailForm.reset();
              this.currentView = 'list';
              this.toastr.success('Address created');
              this.editMode = false;
            },
            error: (err) => {
              console.error('Error creating address:', err);
              this.editMode = false;
            }
          });
        } else if (this.calledInUIC && this.masterId === 0) {
          // UIC Mode without masterId (new company): Add to local list with temp ID and emit
          const tempId = -(this.AddressList.length + 1);
          const newAddress: AddressDetailDTO = {
            addressId: tempId,
            masterId: this.masterId,
            masterType: this.masterType,
            addressType: payload.addressType ?? '',
            address: payload.address ?? '',
            city: payload.city ?? '',
            state: payload.state ?? '',
            country: payload.country ?? '',
            pincode: payload.pincode ?? '',
            status: payload.status ?? 1,
            isActive: payload.isActive ?? 1,
            isDefault: payload.isDefault ?? 0,
            actionUser: this.User.userId.toString(),
            tenantId: this.User.companyId,
            tenantBranchId: this.User.loggedInBranchId,
            isDeleted: 0,
            createdBy: '',
            createdOn: new Date(),
            modifiedBy: '',
            modifiedOn: new Date()
          };
          this.AddressList.push(newAddress);
          this.toastr.success('Address added');
          this.currentView = 'list';
          this.emitAddresses();
          this.editMode = false;
        } else {
          // Normal mode: Call API
          const data: AddressDetailCreateRequestDTO = {
            masterId: ensureNumber(formData.masterId ?? this.masterId),
            masterType: ensureString(formData.masterType ?? this.masterType),
            addressType: ensureString(formData.addressType),
            address: ensureString(formData.address),
            city: ensureString(formData.city),
            state: ensureString(formData.state),
            country: ensureString(formData.country),
            pincode: ensureString(formData.pincode),
            status: ensureNumber(formData.status),
            gstNo: ensureString(formData.gstNo),
            otherDetail1: ensureString(formData.otherDetail1),
            otherDetail2: ensureString(formData.otherDetail2),
            otherDetail3: ensureString(formData.otherDetail3),
            otherDetail4: ensureString(formData.otherDetail4),
            otherDetail5: ensureString(formData.otherDetail5),
            otherDetail6: ensureString(formData.otherDetail6),
            actionUser: this.User.userId.toString(),
            isDefault: isDefaultFlag,
            tenantId: this.User.companyId,
            tenantBranchId: this.User.loggedInBranchId
          };
          this.addressService.AddressDetail_create(data).subscribe(res => {
            this.AddressList.push(res)
            this.addressDetailForm.reset()
            this.editMode = false
            this.toastr.success('Address created');
            this.addressSaved.emit(res)
            // Creating a default address clears the flag on the sibling rows
            // server-side, so resync to avoid showing two "Default" badges.
            if (isDefaultFlag === 1) {
              this.loadAddressDetails();
            }
          })
        }
      }
      this.addressDetailForm.reset();
    }
  }

  defaultCheckBox(event: { rowData: AddressDetailDTO, isChecked: boolean; }) {
    let { rowData, isChecked } = event;
    let payload: AddressDetailUpdateRequestDTO = {
      addressId: rowData.addressId ?? "",
      masterId: rowData.masterId ?? 0,
      masterType: rowData.masterType ?? "",
      addressType: rowData.addressType ?? "",
      address: rowData.address ?? "",
      city: rowData.city ?? "",
      state: rowData.state ?? "",
      country: rowData.country ?? "",
      pincode: rowData.pincode ?? "",
      status: rowData.status ?? 0,
      gstNo: rowData.gstNo || '',
      otherDetail1: rowData.otherDetail1 || '',
      otherDetail2: rowData.otherDetail2 || '',
      otherDetail3: rowData.otherDetail3 || '',
      otherDetail4: rowData.otherDetail4 || '',
      otherDetail5: rowData.otherDetail5 || '',
      otherDetail6: rowData.otherDetail6 || '',
      isActive: 1,
      isDefault: isChecked ? 1 : 0,
      actionUser: this.User.userId.toString(),
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId
    }

    if (this.calledInUIC) {
      // UIC Mode: Update local list only for existing addresses
      const index = this.AddressList.findIndex(a => a.addressId === rowData.addressId);
      if (index >= 0) {
        this.AddressList[index] = { ...this.AddressList[index], isDefault: isChecked ? 1 : 0 };

        // If it's an existing address (addressId > 0), call API immediately
        if (rowData.addressId && rowData.addressId > 0) {
          this.addressService.AddressDetail_update(payload).subscribe({
            next: (res) => {
              this.toastr.success('Default address updated');
            },
            error: (err) => {
              console.error('Error updating address:', err);
            }
          });
        } else {
          // For new addresses (temp ID), just emit
          this.toastr.success('Default address updated');
          this.emitAddresses();
        }
      }
    } else {
      // Normal mode: Call API
      this.addressService.AddressDetail_update(payload).subscribe({
        next: (res) => {
          this.loadAddressDetails()
        },
        error: (err) => {
          console.error('Error updating address:', err);
        }
      })
    }
  }

  addAddress() {
    this.editMode = true
    this.addressDetailForm.reset()

    const patchDefaults = (country: string, state: string, city: string) => {
      this.addressDetailForm.patchValue({
        actionUser: this.User.userId.toString(),
        masterId: this.masterId,
        masterType: this.masterType,
        country,
        state,
        city,
        addressId: 0,
        isActive: 1,
        isDefault: 0,
        status: 0,
        addressType: '',
      })
    }

    this.valuelistService.getItemByVLCode('ADD').subscribe({
      next: (res) => {
        const items: ValueListItemDTO[] = res.valueListItems || []
        const country = items.find(i => i.vliCode.toLowerCase() === 'country')?.vliName || 'India'
        const state = items.find(i => i.vliCode.toLowerCase() === 'state')?.vliName || 'Maharashtra'
        const city = items.find(i => i.vliCode.toLowerCase() === 'city')?.vliName || ''
        patchDefaults(country, state, city)
      },
      error: () => {
        patchDefaults('India', 'Maharashtra', '')
      }
    })
  }

  getStateList(country: CountryDTO) {
    this.locationService.states$.subscribe(res => {
      this.statesList = res.filter(i => i.countryId == country.countryId).sort((a, b) => (a.stateName ?? '').localeCompare(b.stateName ?? ''));
    })
  }

  getCitiesList(State: StateDTO) {
    this.locationService.cities$.subscribe(res => {
      this.citiesList = res.filter(i => i.stateId == State.stateId).sort((a, b) => (a.cityName ?? '').localeCompare(b.cityName ?? ''));
    })
  }

  onCitySelect(city: CitiesDTO) {
    // Add pincode auto-fill logic if available
  }

  // Emit addresses to parent (used by UIC form)
  emitAddresses() {
    if (this.calledInUIC) {
      // Only emit addresses that have temp IDs (addressId < 0)
      const newAddresses = this.AddressList.filter(a => (a.addressId as number) < 0);
      this.AddressDetailFormEmit.emit(newAddresses);
    }
  }

  resetForm() {
    this.editMode = false;
    this.addressDetailForm.reset();
    if (this.formOnly) this.addressCancelled.emit();
  }

  // Get new addresses to create (addresses with temp IDs)
  getNewAddresses(): AddressDetailDTO[] {
    if (this.calledInUIC) {
      return this.AddressList.filter(a => (a.addressId as number) < 0);
    }
    return [];
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}